// src/app/SessionProvider.tsx
'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createClient, hasSupabaseBrowserConfig } from '@/utils/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface SessionContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}

export default function SessionProvider({ children }: { children: React.ReactNode }) {
  const hasSupabaseConfig = useMemo(() => hasSupabaseBrowserConfig(), []);
  const supabase = useMemo(() => {
    if (!hasSupabaseConfig) return null;
    return createClient();
  }, [hasSupabaseConfig]);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      setUser(null);
      setSession(null);
      return;
    }

    let mounted = true;

    // Initialize: onAuthStateChange fires immediately with current session
    // So we don't need a separate getUser() call - onAuthStateChange handles it

    // Listen for auth state changes - this provides both user and session
    // NOTE: onAuthStateChange fires immediately with current session when subscribed
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      // Use session from event directly - it's already verified by Supabase
      // This fires immediately on subscription with the current session
      if (session?.user) {
        setUser(session.user);
        setSession(session);
      } else {
        setUser(null);
        setSession(null);
      }
      
      // Always set loading to false after auth state is determined
      if (mounted) {
        setLoading(false);
      }
    });

    // When the tab becomes visible again after being idle, proactively refresh
    // the session so subsequent API calls don't hit 401.
    const handleVisibilityChange = () => {
      if (!document.hidden && mounted) {
        supabase.auth.refreshSession().catch(() => {
          // Silent — onAuthStateChange will handle the result
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [supabase]); // single subscription
  /* eslint-enable react-hooks/set-state-in-effect */

  const signOut = async () => {
    if (!supabase) return;

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <SessionContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}
